// ---------------------------------------------------------------------------
// Keyboard Shortcuts — binds tinykeys to the EditorStore actions.
//
// Call bindKeyboardShortcuts(element) to attach, returns an unsubscribe fn.
// Uses $mod (Cmd on macOS, Ctrl on Windows/Linux) for cross-platform support.
// ---------------------------------------------------------------------------

// @ts-expect-error — tinykeys exports not resolved under moduleResolution: bundler
import { tinykeys } from 'tinykeys'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { findElementInTree, findParentInTree } from '@/features/page-editor/store/canvas-slice'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { isContainer } from '@/features/page-editor/lib/element-factory'
import { downloadProjectJson, openImportDialog } from '@/features/page-editor/lib/export'
import { toast } from '@/store/toast-store'

/**
 * Bind keyboard shortcuts to the given DOM element.
 * Returns an unsubscribe function that removes all listeners.
 */
/** Check if the keyboard event target is inside a text input */
function isEditingText(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement
  if (!target) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

export function bindKeyboardShortcuts(_element?: HTMLElement): () => void {
  const store = useEditorStore

  // Bind to window so shortcuts work regardless of focus position
  return tinykeys(window, {
    // ---- History (via MCP undo middleware) ----
    '$mod+z': (event: KeyboardEvent) => {
      if (isEditingText(event)) return // Allow native undo in text inputs
      event.preventDefault()
      mcpBus.execute({ tool: 'cogo.studio.undo', params: {} })
    },
    '$mod+Shift+z': (event: KeyboardEvent) => {
      if (isEditingText(event)) return // Allow native redo in text inputs
      event.preventDefault()
      mcpBus.execute({ tool: 'cogo.studio.redo', params: {} })
    },
    '$mod+y': (event: KeyboardEvent) => {
      if (isEditingText(event)) return // Allow native redo in text inputs
      event.preventDefault()
      mcpBus.execute({ tool: 'cogo.studio.redo', params: {} })
    },

    // ---- Selection ----
    '$mod+a': (event: KeyboardEvent) => {
      if (isEditingText(event)) return // Allow native select-all in text inputs
      event.preventDefault()
      store.getState().selectAll()
    },
    Escape: () => {
      const { selectedIds, selectedBlockName, selectionTier, blocks } = store.getState()
      // If component selected, navigate to parent; if already at root/block, clear
      if (selectionTier === 'component' && selectedBlockName && selectedIds.length === 1) {
        const block = blocks[selectedBlockName]
        if (block) {
          const parentInfo = findParentInTree(block.ui.root, selectedIds[0])
          if (parentInfo) {
            // Select the parent element
            store.getState().select(parentInfo.parent.id, selectedBlockName)
            return
          }
        }
      }
      store.getState().clearSelection()
    },

    // ---- Navigation (arrow keys) ----
    ArrowUp: (event: KeyboardEvent) => {
      if (isEditingText(event)) return
      const { selectedIds, selectedBlockName, selectionTier, blocks } = store.getState()
      if (selectionTier !== 'component' || !selectedBlockName || selectedIds.length !== 1) return
      event.preventDefault()
      const block = blocks[selectedBlockName]
      if (!block) return
      const parentInfo = findParentInTree(block.ui.root, selectedIds[0])
      if (!parentInfo || !parentInfo.parent.children) return
      if (parentInfo.index > 0) {
        store.getState().select(parentInfo.parent.children[parentInfo.index - 1].id, selectedBlockName)
      }
    },
    ArrowDown: (event: KeyboardEvent) => {
      if (isEditingText(event)) return
      const { selectedIds, selectedBlockName, selectionTier, blocks } = store.getState()
      if (selectionTier !== 'component' || !selectedBlockName || selectedIds.length !== 1) return
      event.preventDefault()
      const block = blocks[selectedBlockName]
      if (!block) return
      const parentInfo = findParentInTree(block.ui.root, selectedIds[0])
      if (!parentInfo || !parentInfo.parent.children) return
      if (parentInfo.index < parentInfo.parent.children.length - 1) {
        store.getState().select(parentInfo.parent.children[parentInfo.index + 1].id, selectedBlockName)
      }
    },
    Enter: (event: KeyboardEvent) => {
      if (isEditingText(event)) return
      const { selectedIds, selectedBlockName, selectionTier, blocks } = store.getState()
      if (selectionTier !== 'component' || !selectedBlockName || selectedIds.length !== 1) return
      const block = blocks[selectedBlockName]
      if (!block) return
      const element = findElementInTree(block.ui.root, selectedIds[0])
      if (element && isContainer(element) && element.children && element.children.length > 0) {
        event.preventDefault()
        store.getState().select(element.children[0].id, selectedBlockName)
      }
    },

    // ---- Delete ----
    Delete: (event: KeyboardEvent) => {
      if (isEditingText(event)) return // Don't intercept while typing
      event.preventDefault()
      const { selectedIds, selectedBlockName } = store.getState()
      if (selectedBlockName && selectedIds.length > 0) {
        store.getState().removeElements(selectedBlockName, selectedIds)
        store.getState().clearSelection()
      }
    },
    Backspace: (event: KeyboardEvent) => {
      if (isEditingText(event)) return // Don't intercept while typing
      event.preventDefault()
      const { selectedIds, selectedBlockName } = store.getState()
      if (selectedBlockName && selectedIds.length > 0) {
        store.getState().removeElements(selectedBlockName, selectedIds)
        store.getState().clearSelection()
      }
    },

    // ---- Clipboard ----
    '$mod+c': (event: KeyboardEvent) => {
      if (isEditingText(event)) return // Allow normal copy in text inputs
      const { selectedIds } = store.getState()
      if (selectedIds.length > 0) {
        event.preventDefault()
        store.getState().copySelection()
        toast.info(`Copied ${selectedIds.length} element${selectedIds.length > 1 ? 's' : ''}`)
      }
    },
    '$mod+x': (event: KeyboardEvent) => {
      if (isEditingText(event)) return
      const { selectedIds } = store.getState()
      if (selectedIds.length > 0) {
        event.preventDefault()
        store.getState().cutSelection()
        toast.info(`Cut ${selectedIds.length} element${selectedIds.length > 1 ? 's' : ''}`)
      }
    },
    '$mod+v': (event: KeyboardEvent) => {
      if (isEditingText(event)) return
      const { clipboard } = store.getState()
      if (clipboard.elements.length > 0) {
        event.preventDefault()
        store.getState().pasteClipboard()
        toast.info('Pasted')
      }
    },
    '$mod+d': (event: KeyboardEvent) => {
      if (isEditingText(event)) return
      const { selectedIds } = store.getState()
      if (selectedIds.length > 0) {
        event.preventDefault()
        store.getState().duplicateSelection()
        toast.info('Duplicated')
      }
    },

    // ---- Reorder elements ----
    '$mod+ArrowUp': (event: KeyboardEvent) => {
      if (isEditingText(event)) return
      const { selectedIds, selectedBlockName, selectionTier } = store.getState()
      if (selectionTier === 'component' && selectedBlockName && selectedIds.length === 1) {
        event.preventDefault()
        store.getState().moveElement(selectedBlockName, selectedIds[0], 'up')
      }
    },
    '$mod+ArrowDown': (event: KeyboardEvent) => {
      if (isEditingText(event)) return
      const { selectedIds, selectedBlockName, selectionTier } = store.getState()
      if (selectionTier === 'component' && selectedBlockName && selectedIds.length === 1) {
        event.preventDefault()
        store.getState().moveElement(selectedBlockName, selectedIds[0], 'down')
      }
    },

    // ---- Preview toggle ----
    '$mod+Shift+p': (event: KeyboardEvent) => {
      event.preventDefault()
      store.getState().togglePreview()
    },

    // ---- Search panel ----
    '$mod+f': (event: KeyboardEvent) => {
      event.preventDefault()
      store.getState().setActiveRail('search')
      // Focus the search input after the panel renders
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLInputElement>('[data-testid="search-input"]')
        input?.focus()
      })
    },

    // ---- Export / Save ----
    '$mod+s': (event: KeyboardEvent) => {
      event.preventDefault()
      const state = store.getState()
      downloadProjectJson({
        app: state.app,
        blocks: state.blocks,
        appTarget: state.appTarget,
        data: state.data,
        flows: state.flows,
        tokens: state.tokens,
      })
      toast.success('Project exported')
    },

    // ---- Import / Open ----
    '$mod+o': (event: KeyboardEvent) => {
      event.preventDefault()
      openImportDialog()
        .then((loaded) => {
          if (loaded) {
            store.getState().loadApp(loaded)
            toast.success('Project imported')
          }
        })
        .catch((err) => {
          console.error('[Import] Failed:', err)
          toast.error(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
        })
    },

    // ---- Zoom ----
    '$mod+0': (event: KeyboardEvent) => {
      event.preventDefault()
      zoomToFit()
    },
    '$mod+1': (event: KeyboardEvent) => {
      event.preventDefault()
      store.getState().setZoom(1)
    },
    '$mod+Equal': (event: KeyboardEvent) => {
      event.preventDefault()
      const z = store.getState().zoom
      store.getState().setZoom(Math.min(z + 0.1, 3))
    },
    '$mod+Minus': (event: KeyboardEvent) => {
      event.preventDefault()
      const z = store.getState().zoom
      store.getState().setZoom(Math.max(z - 0.1, 0.25))
    },
  })
}

/** Calculate and apply zoom to fit the canvas content */
function zoomToFit() {
  const canvas = document.querySelector<HTMLElement>('[data-testid="canvas-viewport"]')
  if (!canvas) return
  const deviceFrame = canvas.querySelector<HTMLElement>('[data-testid="device-frame"]')
  if (!deviceFrame) return

  const canvasRect = canvas.getBoundingClientRect()
  const currentZoom = useEditorStore.getState().zoom
  // Get the device frame's unscaled width/height
  const frameRect = deviceFrame.getBoundingClientRect()
  const frameW = frameRect.width / currentZoom
  const frameH = frameRect.height / currentZoom
  // Account for padding around the frame (p-8 = 32px on each side)
  const availW = canvasRect.width - 64
  const availH = canvasRect.height - 64
  const fitZoom = Math.min(availW / frameW, availH / frameH, 1)
  const clamped = Math.max(0.25, Math.min(3, Math.round(fitZoom * 100) / 100))
  useEditorStore.getState().setZoom(clamped)
}
